import type { ConnectionStatus } from "@/services/stomp.service";
import type { StompEventResponse } from "@safers/api";
import { SEVERITY_PALETTE } from "@safers/ui";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { eventSeverity, EVENT_SEVERITY_STYLES, type EventSeverityBucket } from "@/services";
import { AlertGlyphIcon } from "@/assets/icons/AlertGlyphIcon";
import aiCircleIcon from "@/assets/icons/ai-circle.svg";

type DisplayLevel = EventSeverityBucket;

const LEVEL_STYLE: Record<DisplayLevel, { badge: string }> = Object.fromEntries(
  (Object.keys(EVENT_SEVERITY_STYLES) as DisplayLevel[]).map((k) => [
    k,
    { badge: `${EVENT_SEVERITY_STYLES[k].bg} ${EVENT_SEVERITY_STYLES[k].text}` },
  ])
) as Record<DisplayLevel, { badge: string }>;

const LEVEL_LABEL: Record<DisplayLevel, string> = {
  critical: EVENT_SEVERITY_STYLES.critical.label,
  warning: EVENT_SEVERITY_STYLES.warning.label,
  caution: EVENT_SEVERITY_STYLES.caution.label,
  attention: EVENT_SEVERITY_STYLES.attention.label,
  uncertain: EVENT_SEVERITY_STYLES.uncertain.label,
  normal: EVENT_SEVERITY_STYLES.normal.label,
};

function formatDateTime(isoStr: string): { date: string; time: string } {
  const d = new Date(isoStr);
  return {
    date: d.toLocaleDateString("ko-KR", { month: "2-digit", day: "2-digit" }),
    time: d.toLocaleTimeString("ko-KR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }),
  };
}

interface EventRowProps {
  event: StompEventResponse;
  isSelected: boolean;
  isLive: boolean;
  isUnread: boolean;
  onSelect: (event: StompEventResponse) => void;
}

function EventRow({ event, isSelected, isLive, isUnread, onSelect }: EventRowProps) {
  const label = event.name;
  const level = eventSeverity(event);
  const style = LEVEL_STYLE[level];
  const hasVideo = !!event.video?.url;
  const hasSnapshot = !!event.snapshot?.url;
  const { date, time } = formatDateTime(event.timestamp);
  const accent = (level === "critical" ? SEVERITY_PALETTE.CRITICAL : SEVERITY_PALETTE.WARNING).fill;

  return (
    <button
      type="button"
      onClick={() => onSelect(event)}
      aria-label={`${label} - ${date} ${time}`}
      className={`enabled:cursor-pointer disabled:cursor-not-allowed relative flex w-full gap-3 border-b border-neutral-200 dark:border-[#2A2D3A] py-3 pl-4 pr-4 text-left transition-colors last:border-b-0 hover:bg-white dark:hover:bg-[#1E2130] ${
        isSelected ? "bg-white dark:bg-[#1E2130] ring-1 ring-inset ring-brand/50" : ""
      }`}
    >
      {isUnread && (
        <span
          aria-label="미확인"
          style={
            {
              "--safelog-accent": accent,
              boxShadow:
                "inset 0 0 1rem 0 color-mix(in srgb, var(--safelog-accent) 50%, transparent)",
            } as CSSProperties
          }
          className={`pointer-events-none absolute inset-0 ${
            isLive ? "motion-safe:animate-[safelog-arrive_1.6s_ease-in-out_infinite]" : ""
          }`}
        />
      )}
      <div className="relative h-[4.5rem] w-[6.5rem] shrink-0 overflow-hidden rounded-md bg-neutral-100 dark:bg-[#252833]">
        {hasSnapshot ? (
          <img src={event.snapshot!.url} alt={label} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <svg
              className="h-6 w-6 text-neutral-400 dark:text-white/20"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}

        <div className="absolute bottom-1 right-1">
          {hasVideo ? (
            <span className="inline-flex items-center gap-0.5 rounded bg-green-500/90 px-1.5 py-0.5 text-[0.5625rem] font-bold text-white">
              <svg className="h-2.5 w-2.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
              영상
            </span>
          ) : (
            <span className="inline-flex items-center gap-0.5 rounded bg-black/60 px-1.5 py-0.5 text-[0.5625rem] text-white/60 backdrop-blur-sm">
              <svg
                className="h-2.5 w-2.5 animate-spin"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              제작중
            </span>
          )}
        </div>
      </div>

      <div className="relative flex min-w-0 flex-1 flex-col justify-between py-0.5">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-0.5 text-xs font-bold ${style.badge}`}
            >
              <AlertGlyphIcon size={12} className="shrink-0" />
              {LEVEL_LABEL[level]}
            </span>
            {event.confidence != null && (
              <span className="ml-auto shrink-0 text-xs text-neutral-400 dark:text-white/30">
                {(event.confidence * 100).toFixed(0)}%
              </span>
            )}
          </div>
          <span className="text-sm font-medium text-neutral-800 dark:text-white/90">{label}</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="shrink-0 font-mono text-xs text-neutral-500 dark:text-white/40">
            {date} {time}
          </span>
        </div>
      </div>
    </button>
  );
}

interface EventLogPanelProps {
  events: StompEventResponse[];
  liveIds?: ReadonlySet<string>;
  unreadIds?: ReadonlySet<string>;
  unreadCount?: number;
  onMarkAllSeen?: () => void;
  isLoading: boolean;
  isSearching?: boolean;
  query: string;
  onQueryChange: (q: string) => void;
  selectedEventId: string | null;
  onSelectEvent: (event: StompEventResponse) => void;
  pendingCount?: number;
  onRefreshToLatest?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  connectionStatus?: ConnectionStatus;
}

export function EventLogPanel({
  events,
  liveIds,
  unreadIds,
  unreadCount,
  onMarkAllSeen,
  isLoading,
  isSearching,
  query,
  onQueryChange,
  selectedEventId,
  onSelectEvent,
  pendingCount,
  onRefreshToLatest,
  hasMore,
  isLoadingMore,
  onLoadMore,
  connectionStatus,
}: EventLogPanelProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState(query);
  const [autoScroll, setAutoScroll] = useState(true);

  const [prevQuery, setPrevQuery] = useState(query);
  if (prevQuery !== query) {
    setPrevQuery(query);
    if (query === "") setInputValue("");
  }

  useEffect(() => {
    if (autoScroll && listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [events.length, autoScroll]);

  function handleScroll() {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    setAutoScroll(scrollTop < 10);

    if (scrollHeight - scrollTop - clientHeight < 150) {
      onLoadMore?.();
    }
  }

  const counts: Record<DisplayLevel, number> = {
    critical: 0,
    warning: 0,
    caution: 0,
    attention: 0,
    uncertain: 0,
    normal: 0,
  };
  for (const e of events) {
    counts[eventSeverity(e)]++;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-neutral-200 dark:border-[#2A2D3A] p-3">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex min-w-0 items-center gap-1.5 text-base font-bold text-neutral-900 dark:text-white">
            <img src={aiCircleIcon} alt="" className="h-5 w-5 invert dark:invert-0" />
            <span className="text-lg flex-shrink-0 text-neutral-900 dark:text-white leading-[unset]">
              SafeLog
            </span>
            {connectionStatus && (
              <span className="ml-1.5 flex min-w-0 items-center gap-1.5">
                <span
                  className={`h-2 w-2 flex-none rounded-full ${
                    connectionStatus === "connected"
                      ? "bg-green-400"
                      : connectionStatus === "connecting"
                        ? "animate-pulse bg-yellow-400"
                        : "bg-red-400"
                  }`}
                />
                <span className="truncate text-xs font-medium text-neutral-500 dark:text-white/40">
                  {connectionStatus === "connected" ? "AI 엔진 연결됨" : "연결 대기"}
                </span>
              </span>
            )}
          </h2>
          <div className="flex shrink-0 items-center gap-1">
            {onMarkAllSeen && (unreadCount ?? 0) > 0 && (
              <button
                type="button"
                onClick={onMarkAllSeen}
                title={`미확인 ${unreadCount}건을 모두 확인 처리`}
                className="enabled:cursor-pointer disabled:cursor-not-allowed rounded-md border border-neutral-200 px-2 py-1 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-100 dark:border-[#2A2D3A] dark:text-white/60 dark:hover:bg-white/10"
              >
                모두 확인
              </button>
            )}
          </div>
        </div>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2">
            {isSearching ? (
              <svg className="h-4 w-4 animate-spin text-brand" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
            ) : (
              <img src={aiCircleIcon} alt="" className="h-4 w-4 opacity-50 invert dark:invert-0" />
            )}
          </div>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onQueryChange(inputValue);
            }}
            placeholder="자연어로 검색  예) 어제 안전모 미착용"
            className="w-full rounded-md border border-neutral-200 dark:border-[#2A2D3A] bg-white dark:bg-[#252833] py-2 pl-9 pr-8 text-sm text-neutral-900 dark:text-white placeholder:text-neutral-400 dark:placeholder:text-white/25 outline-none hover:border-neutral-300 focus:border-brand/60 transition-colors"
          />
          {inputValue && (
            <button
              onClick={() => {
                setInputValue("");
                if (onRefreshToLatest) onRefreshToLatest();
                else onQueryChange("");
              }}
              className="enabled:cursor-pointer disabled:cursor-not-allowed absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-white/30 hover:text-neutral-600 dark:hover:text-white/60"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-neutral-200 dark:border-[#2A2D3A] px-3 py-2">
        <div className="flex items-center gap-1.5 rounded-md bg-red-500/10 px-2 py-1">
          <span className="h-2 w-2 shrink-0 rounded-full bg-red-500" />
          <span className="text-xs font-bold text-red-700 dark:text-red-400">
            {counts.critical}
          </span>
        </div>
        <div className="flex items-center gap-1.5 rounded-md bg-orange-500/10 px-2 py-1">
          <span className="h-2 w-2 shrink-0 rounded-full bg-orange-500" />
          <span className="text-xs font-bold text-orange-700 dark:text-orange-400">
            {counts.warning}
          </span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-neutral-500 dark:text-white/40">{`전체 ${events.length}건`}</span>
          {!autoScroll && (
            <button
              onClick={() => {
                setAutoScroll(true);
                if (listRef.current) listRef.current.scrollTop = 0;
              }}
              className="enabled:cursor-pointer disabled:cursor-not-allowed text-xs text-brand hover:text-brand/80"
            >
              최신으로
            </button>
          )}
        </div>
      </div>

      {(pendingCount ?? 0) > 0 && (
        <button
          onClick={onRefreshToLatest}
          className="enabled:cursor-pointer disabled:cursor-not-allowed flex w-full items-center justify-center gap-2 border-b border-neutral-200 dark:border-[#2A2D3A] bg-brand/10 py-2 text-xs font-bold text-brand hover:bg-brand/20 transition-colors"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-brand animate-pulse" />새 이벤트{" "}
          {pendingCount}건 · 최신으로
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}

      <div ref={listRef} onScroll={handleScroll} className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-200 dark:border-white/20 border-t-neutral-900 dark:border-t-white" />
          </div>
        ) : events.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-sm text-neutral-400 dark:text-white/30">
            {query ? "검색 결과가 없습니다" : "이벤트 대기 중..."}
          </div>
        ) : (
          events.map((event) => (
            <EventRow
              key={event.eventId}
              event={event}
              isSelected={event.eventId === selectedEventId}
              isLive={liveIds?.has(event.eventId) ?? false}
              isUnread={unreadIds?.has(event.eventId) ?? false}
              onSelect={onSelectEvent}
            />
          ))
        )}

        {!isLoading &&
          (isLoadingMore ? (
            <div className="flex items-center justify-center py-4">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-200 dark:border-white/20 border-t-neutral-900 dark:border-t-white" />
            </div>
          ) : !hasMore && events.length > 0 ? (
            <div className="py-4 text-center text-xs text-neutral-400 dark:text-white/20">
              모든 이벤트를 불러왔습니다
            </div>
          ) : null)}
      </div>
    </div>
  );
}
